import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
] as const;

type LangCode = (typeof LANGUAGES)[number]['code'];

const normLang = (lng: string | undefined): LangCode =>
  lng?.slice(0, 2) === 'fr' ? 'fr' : 'en';

interface Props {
  variant?: 'light' | 'dark';
}

export const LanguageSwitcher: React.FC<Props> = ({ variant = 'dark' }) => {
  const { i18n } = useTranslation();

  const [currentLang, setCurrentLang] = useState<LangCode>(() =>
    normLang(i18n.resolvedLanguage ?? i18n.language)
  );

  // Mirror external i18n changes (e.g. programmatic changeLanguage from elsewhere)
  useEffect(() => {
    const handler = (lng: string) => setCurrentLang(normLang(lng));
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n]);

  // Drive i18n from local state — runs after the button commit so the button
  // visually updates in one frame before the heavy dashboard re-render cascade.
  useEffect(() => {
    if (normLang(i18n.language) !== currentLang) {
      document.documentElement.lang = currentLang;
      void i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  const handleChange = (e: React.MouseEvent, code: LangCode) => {
    e.preventDefault();
    e.stopPropagation();
    if (code === currentLang) return;
    setCurrentLang(code);
    document.documentElement.lang = code;
    localStorage.setItem('brandedge_language', code);
  };

  const isLight = variant === 'light';

  return (
    <div
      className="relative z-10 flex items-center gap-0.5 rounded-lg border border-current/20 p-0.5 pointer-events-auto select-none"
      role="group"
      aria-label="Select language"
    >
      {LANGUAGES.map(({ code, label }) => {
        const isActive = currentLang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={(e) => handleChange(e, code)}
            aria-pressed={isActive}
            aria-label={code === 'en' ? 'English' : 'Français'}
            className={[
              'cursor-pointer select-none text-[10px] font-bold uppercase tracking-widest rounded-md',
              'px-3 py-2 min-h-[36px] min-w-[36px] flex items-center justify-center',
              'pointer-events-auto transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current',
              isLight
                ? isActive
                  ? 'bg-white text-slate-900'
                  : 'text-white/70 hover:text-white'
                : isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-[#94A3B8] hover:text-[#475569]',
            ].join(' ')}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};
